import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import "../homeDashboard/DashboardSkeleton.css";
import {
  useQuestionBankQuestionFeedback,
  useQuestionReports,
} from "../hooks/useQuestionBankQuestionFeedback";

/* =========================================
   REPORT MODAL STYLES
========================================= */

const ReportModalStyles = () => (
  <style>{`
    .report-modal-overlay {
      position: fixed;
      inset: 0;
      display: flex;
      justify-content: flex-end;
      align-items: stretch;
      padding: 0;
      background: rgba(0, 0, 0, 0.3);
      z-index: 9999;
      animation: reportOverlayFadeIn 0.2s ease;
    }
    .report-modal,
    .report-modal * {
      box-sizing: border-box;
    }
    .report-modal {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 420px;
      gap: 0px;
      max-width: 95%;
      height: 100vh;
      height: 100dvh;
      max-height: 100%;
      min-height: 0;
      padding: 15px;
      background: #fff;
      border-radius: 0;
      box-shadow: -8px 0 30px rgba(0, 0, 0, 0.08);
      animation: reportSidebarIn 0.3s ease;
      overflow: hidden;
    }

    .report-modal__drag-handle {
      display: none;
    }

    .report-modal__header {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-shrink: 0;
      padding: 0 10px 20px;
      background: #fff;
      border-bottom: 1px solid #edf0f4;
    }

    .report-modal__title-group {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }

    .report-modal__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      flex-shrink: 0;
      color: #4d1fe0;
      background: #d8ccfe;
      border-radius: 50%;
      font-size: 19px;
    }

    .report-modal__title {
      margin: 0;
      color: #172033;
      font-size: 19px;
      font-weight: 700;
      line-height: 1.4;
    }

    .report-modal__close {
      color: #000;
      background: transparent;
      border: none;
      box-shadow: none;
    }

    .report-modal__close .view-close {
      display: block;
      line-height: 1;
    }

    /* Body takes remaining height */
    .report-modal__body {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: 12px;
      min-height: 0;
      padding: 16px 10px 8px;
      overflow: hidden;
    }

    /* Only the reports list scrolls */
    .report-modal__list {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: 14px;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      scrollbar-width: thin;
      scrollbar-color: #cbd5e1 transparent;
      max-height: unset;
    }

    .report-modal__list::-webkit-scrollbar {
      width: 5px;
    }

    .report-modal__list::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 10px;
    }

    .report-modal__list::-webkit-scrollbar-track {
      background: transparent;
    }

    .report-card {
      flex-shrink: 0;
      min-width: 0;
      padding: 14px;
      background: #fff;
      border: 1px solid #e8edf3;
      border-radius: 12px;
    }

    .report-card__top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 12px;
    }

    .report-card__heading {
      display: flex;
      align-items: center;
      gap: 9px;
      min-width: 0;
    }

    .report-card__index {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      flex-shrink: 0;
      color: #4d1fe0;
      background: #eee8ff;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 700;
    }

    .report-card__title {
      margin: 0;
      color: #172033;
      font-size: 14px;
      font-weight: 700;
    }

    .report-card__dismiss {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      color: #dc2626;
      background: #fff1f2;
      border: none;
      border-radius: 8px;
      cursor: pointer;
    }

    .report-card__dismiss:hover:not(:disabled) {
      background: #ffe4e6;
    }

    .report-card__dismiss:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .report-card__text {
      margin: 0;
      color: #475569;
      font-size: 13px;
      line-height: 1.7;
      overflow-wrap: anywhere;
      white-space: pre-wrap;
    }

    .report-card__reply {
      margin-top: 14px;
      padding: 12px;
      background: #f8fafc;
      border-left: 3px solid #4f46e5;
      border-radius: 8px;
    }

    .report-card__reply-title {
      margin: 0 0 7px;
      color: #3730a3;
      font-size: 13px;
      font-weight: 700;
    }

    .report-card__reply-body {
      min-width: 0;
    }

    .report-card__reply-text {
      margin: 0;
      color: #475569;
      font-size: 13px;
      line-height: 1.65;
      overflow-wrap: anywhere;
      white-space: pre-wrap;
    }

    /* Compose stays at the bottom, above Save */
    .report-modal__compose {
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      gap: 10px;
      margin-top: auto;
      padding: 0;
    }

    .report-modal__compose-label {
      font-weight: 600;
    }

    .report-modal__compose-label i {
      color: #c50217;
      font-size: 17px;
    }

    .report-modal__textarea {
      display: block;
      width: 100%;
      min-height: 60px;
      max-height: 150px;
      padding: 12px 14px;
      color: #1f2937;
      background: #fff;
      border: 1px solid #dbe2ea;
      border-radius: 12px;
      outline: none;
      font-family: inherit;
      font-size: 14px;
      line-height: 1.6;
      resize: vertical;
      transition: border-color 0.2s, box-shadow 0.2s;
    }

    .report-modal__textarea::placeholder {
      color: #94a3b8;
    }

    .report-modal__textarea:focus {
      border-color: #647cf3;
      box-shadow: 0 0 0 3px rgba(100, 124, 243, 0.12);
    }

    /* Save footer remains below the textarea */
    .report-modal__footer {
      position: relative;
      z-index: 2;
      display: flex;
      flex: 0 0 auto;
      justify-content: flex-end;
      align-items: center;
      padding: 12px 10px;
      padding-bottom: calc(12px + env(safe-area-inset-bottom));
      background: #fff;
      border-top: 1px solid #edf0f4;
    }

    .report-modal__save {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 110px;
      min-height: 44px;
      padding: 11px 24px;
      color: #fff;
      background: #4f46e5;
      border: none;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s, opacity 0.2s;
    }

    .report-modal__save:hover:not(:disabled) {
      background: #4338ca;
    }

    .report-modal__save:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    .report-modal__corner {
      position: absolute;
      z-index: 3;
      width: 16px;
      height: 16px;
      pointer-events: none;
    }

    .report-modal__corner--top {
      top: 0;
      left: 0;
      border-top: 2px solid #647cf3;
      border-left: 2px solid #647cf3;
    }

    .report-modal__corner--bottom {
      right: 0;
      bottom: 0;
      border-right: 2px solid #647cf3;
      border-bottom: 2px solid #647cf3;
    }

    @keyframes reportOverlayFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes reportSidebarIn {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
    }

    @keyframes reportBottomSheetIn {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
.report-modal__list:has(.report-modal__empty) {
  flex: 1;
  justify-content: center;
  align-items: center;
}

.report-modal__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100%;
  text-align: center;
  color: #64748b;
  font-size: 14px;
}

    
    /* MOBILE BOTTOM SHEET */
    @media (max-width: 576px) {
      .report-modal-overlay {
        align-items: flex-end;
        justify-content: center;
        padding: 0;
      }

      .report-modal {
        position: relative;
        top: auto;
        right: auto;
        bottom: 0;
        width: 100%;
        max-width: 100%;
        height: 65vh;
        height: 65dvh;
        max-height: 96dvh;
        padding: 0 18px env(safe-area-inset-bottom);
        border-radius: 20px 20px 0 0;
        box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.16);
        animation: reportBottomSheetIn 0.3s ease both;
        transition: height 0.25s ease, max-height 0.25s ease;
            gap: 8px;
      }

      .report-modal.report-modal--expanded {
        height: 96dvh;
        max-height: 96dvh;
      }

      .report-modal.report-modal--dragging {
        transition: none;
        animation: none;
      }

      .report-modal__drag-handle {
        display: flex;
        flex: 0 0 28px;
        align-items: center;
        justify-content: center;
        position: relative;
        z-index: 5;
        width: 100%;
        height: 28px;
        padding: 0;
        background: #fff;
        border: none;
        border-radius: 20px 20px 0 0;
        cursor: grab;
        touch-action: none;
        user-select: none;
        -webkit-user-select: none;
      }

      .report-modal__drag-handle:active {
        cursor: grabbing;
      }

      .report-modal__drag-indicator {
        display: block;
        width: 42px;
        height: 4px;
        background: #cbd5e1;
        border-radius: 10px;
      }

      .report-modal__header {
        padding: 8px 0 14px;
      }

      .report-modal__icon {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        font-size: 17px;
      }

      .report-modal__title {
        font-size: 17px;
      }

      .report-modal__close {
        width: 34px;
        height: 34px;
      }

      .report-modal__body {
        gap: 10px;
        padding: 4px 0 6px;
      }

      .report-modal__list {
        gap: 12px;
      }

      .report-card {
        padding: 12px;
      }

      .report-modal__compose {
        gap: 8px;
        margin-top: auto;
      }

      .report-modal__compose-label {
        font-size: 13px;
      }

      .report-modal__textarea {
        min-height: 60px;
        max-height: 110px;
        padding: 10px 12px;
        resize: none;
        font-size: 13px;
      }

      .report-modal__footer {
        padding: 10px 0;
        padding-bottom: calc(10px + env(safe-area-inset-bottom));
      }

      .report-modal__save {
        width: 100%;
        min-height: 46px;
      }

      .report-modal__corner--bottom {
        display: none;
      }
    }

    @media (min-width: 577px) and (max-width: 766px) {
      .report-modal {
        width: 100%;
        max-width: 100%;
      }
    }

    @media (min-width: 767px) {
      .report-modal__drag-handle {
        display: none;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .report-modal-overlay,
      .report-modal {
        animation: none !important;
      }
    }
  `}</style>
);

/* =========================================
   LOADING SHIMMER
========================================= */

const FeedbackModalShimmer = () => (
  <>
    {Array.from({ length: 3 }).map((_, index) => (
      <article className="report-card" key={`report-shimmer-${index}`}>
        <div className="report-card__top">
          <div className="report-card__heading">
            <span className="dashboard-skeleton dashboard-skeleton--icon report-modal__shimmer-index" />
            <span className="dashboard-skeleton dashboard-skeleton--line report-modal__shimmer-title" />
          </div>
          <span className="dashboard-skeleton dashboard-skeleton--icon report-modal__shimmer-action" />
        </div>
        <span className="dashboard-skeleton dashboard-skeleton--line report-modal__shimmer-line" />
        <span className="dashboard-skeleton dashboard-skeleton--line report-modal__shimmer-line report-modal__shimmer-line--short" />
      </article>
    ))}
  </>
);

/* =========================================
   REPORT MODAL
========================================= */

const ReportModal = ({
  open,
  onClose,
  question = null,
  questionQueryParams = {},
}) => {
  const [reportText, setReportText] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dragY, setDragY] = useState(0);

  const dragStartY = useRef(0);
  const currentDragY = useRef(0);
  const pointerId = useRef(null);

  const questionId = Number(question?.id || 0);

  const reportsQuery = useQuestionReports(questionId, {
    enabled: open && Boolean(questionId),
  });

  const { createReport, deleteReport } =
    useQuestionBankQuestionFeedback(questionQueryParams);

  const reportsLoading =
    (reportsQuery.isLoading || reportsQuery.isFetching) &&
    (!reportsQuery.data || reportsQuery.isPlaceholderData);

  useEffect(() => {
    setReportText("");
  }, [open, questionId]);

  useEffect(() => {
    if (open) {
      setIsDragging(false);
      setIsExpanded(false);
      setDragY(0);
      currentDragY.current = 0;
      pointerId.current = null;
    }
  }, [open, questionId]);

  /* Lock background scrolling and support Escape */
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  /* =========================================
     DRAG HANDLERS
  ========================================= */

  const handleDragStart = (event) => {
    if (window.innerWidth >= 767) return;
    if (event.button !== undefined && event.button !== 0) return;

    dragStartY.current = event.clientY;
    currentDragY.current = 0;
    pointerId.current = event.pointerId;

    setDragY(0);
    setIsDragging(true);

    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleDragMove = (event) => {
    if (pointerId.current !== event.pointerId) return;

    const deltaY = event.clientY - dragStartY.current;
    currentDragY.current = deltaY;

    setDragY(deltaY > 0 ? deltaY : 0);
  };

  const handleDragEnd = (event) => {
    if (pointerId.current !== event.pointerId) return;

    const deltaY = currentDragY.current;

    setIsDragging(false);
    setDragY(0);
    currentDragY.current = 0;
    pointerId.current = null;

    if (deltaY > 100) {
      onClose?.();
    } else if (deltaY < -45) {
      setIsExpanded(true);
    }
  };

  const handleDragCancel = () => {
    setIsDragging(false);
    setDragY(0);
    currentDragY.current = 0;
    pointerId.current = null;
  };

  /* =========================================
     SAVE REPORT
  ========================================= */

  const handleSave = async () => {
    const message = reportText.trim();

    if (!message || !questionId || createReport.isPending) return;

    try {
      await createReport.mutateAsync({
        question_id: questionId,
        message,
      });

      setReportText("");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Save Report",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
      });
    }
  };

  /* =========================================
     DELETE REPORT
  ========================================= */

  const handleDeleteReport = async (item) => {
    const reportId = Number(item?.id || 0);

    if (!reportId || deleteReport.isPending) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Report?",
      text: "This report will be removed permanently for this question.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e53935",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteReport.mutateAsync({
        report_id: reportId,
        question_id: questionId,
      });

      await Swal.fire({
        icon: "success",
        title: "Report Deleted",
        text: "Your report has been removed successfully.",
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Delete Report",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
      });
    }
  };

  const modalStyle =
    window.innerWidth < 767 && dragY > 0
      ? { transform: `translateY(${dragY}px)` }
      : undefined;

  return (
    <>
      <ReportModalStyles />

      <div
        className="report-modal-overlay"
        role="presentation"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            onClose?.();
          }
        }}
      >
        <aside
          className={[
            "report-modal",
            isExpanded ? "report-modal--expanded" : "",
            isDragging ? "report-modal--dragging" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={modalStyle}
          role="dialog"
          aria-modal="true"
          aria-label="Your Reports"
          onClick={(event) => event.stopPropagation()}
        >
          {/* MOBILE DRAG HANDLE */}
          <div
            className="report-modal__drag-handle"
            role="separator"
            aria-label="Drag up to expand or down to close reports"
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragCancel}
            onLostPointerCapture={handleDragCancel}
            onDoubleClick={() => setIsExpanded((previous) => !previous)}
          >
            <span className="report-modal__drag-indicator" />
          </div>

          {/* HEADER */}
          <div className="report-modal__header">
            <div className="report-modal__title-group">
              <div className="report-modal__icon" aria-hidden="true">
                <i className="bi bi-exclamation-triangle-fill" />
              </div>

              <h3 className="report-modal__title">Your Report(s)</h3>
            </div>

            <button
              type="button"
              className="report-modal__close"
              onClick={() => onClose?.()}
              aria-label="Close report panel"
            >
              <span className="view-close" aria-hidden="true">
                ×
              </span>
            </button>
          </div>

          {/* BODY */}
          <div className="report-modal__body">
            {/* SCROLLABLE REPORTS LIST */}
            <div className="report-modal__list" aria-label="Reported questions">
              {reportsLoading && <FeedbackModalShimmer />}

              {!reportsLoading && reportsQuery.error && (
                <div className="choose-state__status choose-state__status--error">
                  {reportsQuery.error?.response?.data?.message ||
                    reportsQuery.error?.message ||
                    "Unable to load reports."}
                </div>
              )}

              {!reportsLoading &&
                !reportsQuery.error &&
                !(reportsQuery.data?.items || []).length && (
                  <div className="choose-state__status report-modal__empty">
                    No reports available.
                  </div>
                )}

              {!reportsLoading &&
                !reportsQuery.error &&
                (reportsQuery.data?.items || []).map((item, index) => (
                  <article className="report-card" key={item.id}>
                    <div className="report-card__top">
                      <div className="report-card__heading">
                        <span className="report-card__index">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        {/* <h4 className="report-card__title">Your Query</h4> */}
                      </div>

                      <button
                        type="button"
                        className="report-card__dismiss"
                        onClick={() => handleDeleteReport(item)}
                        disabled={deleteReport.isPending}
                        aria-label="Delete report"
                      >
                        <i className="bi bi-trash3" aria-hidden="true" />
                      </button>
                    </div>

                    <p className="report-card__text">{item.description}</p>

                    {item.replyDescription && (
                      <section className="report-card__reply">
                        <h5 className="report-card__reply-title">
                          Admin Reply
                        </h5>

                        <div className="report-card__reply-body">
                          <p className="report-card__reply-text">
                            {item.replyDescription}
                          </p>
                        </div>
                      </section>
                    )}
                  </article>
                ))}
            </div>

            {/* COMPOSE: FIXED NEAR SAVE BUTTON */}
            <div className="report-modal__compose">
              <div className="report-modal__compose-label">
                <i className="bi bi-file-earmark-text" aria-hidden="true" />
                <span>Report This Question</span>
              </div>

              <textarea
                className="report-modal__textarea"
                placeholder="Report This Question"
                value={reportText}
                onChange={(event) => setReportText(event.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* FOOTER: SAVE BUTTON BELOW COMPOSE */}
          <div className="report-modal__footer">
            <button
              type="button"
              className="report-modal__save"
              onClick={handleSave}
              disabled={
                !reportText.trim() || !questionId || createReport.isPending
              }
            >
              {createReport.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </aside>
      </div>
    </>
  );
};

export default ReportModal;
